import type { Client } from '@connectrpc/connect';
import { ChangeStatus, type Note, type NoteService } from '@/gen/tagn/v1/notes_pb';
import { changeFromLocal, noteFromProto } from '@/api/mapping';
import type { LocalNote } from '@/domain/note';
import type { TagnDB } from './db';
import { NotesRepository } from './notes';

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'error';

export interface SyncState {
  status: SyncStatus;
  lastSyncAt: number | null;
  conflicts: number;
}

const CURSOR_KEY = 'syncCursor';
const BATCH = 200;
/** Deletions are delayed so that "undo" can still restore them locally. */
export const DELETE_GRACE_MS = 6_000;
/** Upper bound for a debounced sync while edits keep coming in. */
export const MAX_SCHEDULE_DELAY_MS = 5_000;
/** A hanging request (flaky mobile network) must not stall syncing forever. */
const REQUEST_TIMEOUT_MS = 20_000;
/** Retry delay when another tab of the app is currently syncing. */
const LOCK_RETRY_MS = 2_000;
const LOCK_NAME = 'tagn-sync';

interface Options {
  now?: () => number;
  isOnline?: () => boolean;
  ownerId: () => string;
}

/**
 * Offline-first synchronisation: pushes dirty local notes, then pulls all
 * changes since the stored cursor. Version conflicts keep the local text as a
 * separate "conflict copy" note and accept the server state.
 */
export class SyncEngine {
  private state: SyncState = { status: 'idle', lastSyncAt: null, conflicts: 0 };
  private readonly listeners = new Set<(s: SyncState) => void>();
  private running: Promise<void> | null = null;
  private rerun = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private deadline: number | null = null;
  private readonly repo: NotesRepository;
  private readonly now: () => number;
  private readonly isOnline: () => boolean;

  constructor(
    private readonly db: TagnDB,
    private readonly client: Client<typeof NoteService>,
    private readonly opts: Options,
  ) {
    this.now = opts.now ?? Date.now;
    this.isOnline = opts.isOnline ?? (() => navigator.onLine);
    this.repo = new NotesRepository(db, this.now);
  }

  getState(): SyncState {
    return this.state;
  }

  subscribe(listener: (s: SyncState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setState(patch: Partial<SyncState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l(this.state));
  }

  /**
   * Debounced sync, e.g. after local edits. A steady stream of edits postpones
   * the sync by at most MAX_SCHEDULE_DELAY_MS (or `delay`, if longer).
   */
  schedule(delay = 1500): void {
    const now = Date.now();
    this.deadline = Math.min(this.deadline ?? Infinity, now + Math.max(delay, MAX_SCHEDULE_DELAY_MS));
    clearTimeout(this.timer);
    this.timer = setTimeout(
      () => {
        this.deadline = null;
        void this.sync();
      },
      Math.min(delay, this.deadline - now),
    );
  }

  /** Runs a sync; concurrent calls are coalesced into one follow-up run. */
  sync(): Promise<void> {
    if (this.running) {
      this.rerun = true;
      return this.running;
    }
    this.running = this.loop().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async loop(): Promise<void> {
    do {
      this.rerun = false;
      if (!this.isOnline()) {
        this.setState({ status: 'offline' });
        return;
      }
      this.setState({ status: 'syncing' });
      try {
        const ran = await this.exclusive(() => this.roundTrips());
        if (!ran) {
          // Another tab syncs the shared database right now; try again shortly.
          this.setState({ status: 'idle' });
          this.schedule(LOCK_RETRY_MS);
          return;
        }
        this.setState({ status: 'idle', lastSyncAt: this.now() });
      } catch {
        this.setState({ status: this.isOnline() ? 'error' : 'offline' });
        return;
      }
    } while (this.consumeRerun());
  }

  private consumeRerun(): boolean {
    return this.rerun;
  }

  /**
   * Runs `fn` while holding a cross-tab lock, so that several open tabs (or the
   * installed app next to a browser tab) never push the same notes concurrently,
   * which would produce spurious conflict copies. Returns false if another tab
   * holds the lock. The UI never waits for this lock.
   */
  private async exclusive(fn: () => Promise<void>): Promise<boolean> {
    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
    if (!locks) {
      await fn();
      return true;
    }
    return locks.request(LOCK_NAME, { ifAvailable: true }, async (lock) => {
      if (!lock) return false;
      await fn();
      return true;
    });
  }

  /**
   * Push/pull round trips. Local reads and writes are short IndexedDB
   * transactions; the network request never runs inside a transaction, so the
   * UI can read, edit and create notes at any time during a sync.
   */
  private async roundTrips(): Promise<void> {
    for (;;) {
      const graceLimit = this.now() - DELETE_GRACE_MS;
      const allDirty = await this.db.notes.where('dirty').equals(1).toArray();
      const dirty = allDirty.filter((n) => !n.deleted || n.updatedAt <= graceLimit).slice(0, BATCH);
      const cursor = (await this.db.getMeta(CURSOR_KEY)) ?? '';
      const res = await this.client.sync({ cursor, changes: dirty.map(changeFromLocal) }, { timeoutMs: REQUEST_TIMEOUT_MS });
      const pushed = new Map(dirty.map((n) => [n.id, n]));
      // One transaction per response: a single change notification for the
      // live queries instead of one UI refresh per note.
      let conflicts = 0;
      await this.db.transaction('rw', this.db.notes, this.db.meta, async () => {
        for (const result of res.results) {
          if (await this.applyResult(result.id, result.status, result.note, pushed.get(result.id))) conflicts++;
        }
        await this.applyPull(res.notes, res.removedIds);
        await this.db.setMeta(CURSOR_KEY, res.cursor);
      });
      if (conflicts > 0) this.setState({ conflicts: this.state.conflicts + conflicts });
      const morePush = dirty.length === BATCH;
      if (!res.hasMore && !morePush) {
        this.scheduleGraceDeletes(allDirty, graceLimit);
        return;
      }
    }
  }

  /** Pushes deletions that were still within their undo grace period once it expires. */
  private scheduleGraceDeletes(dirty: LocalNote[], graceLimit: number): void {
    const waiting = dirty.filter((n) => n.deleted && n.updatedAt > graceLimit);
    if (waiting.length === 0) return;
    const next = Math.min(...waiting.map((n) => n.updatedAt)) - graceLimit;
    this.schedule(next + 500);
  }

  /** Applies one push result inside the response transaction. Returns true for a conflict copy. */
  private async applyResult(id: string, status: ChangeStatus, server: Note | undefined, pushed: LocalNote | undefined): Promise<boolean> {
    if (!pushed) return false;
    switch (status) {
      case ChangeStatus.APPLIED:
        await this.applyApplied(id, server, pushed);
        return false;
      case ChangeStatus.CONFLICT:
        return this.applyConflict(server, pushed);
      case ChangeStatus.FORBIDDEN:
        await this.replaceOrDrop(id, server);
        return false;
      default:
        // Invalid change: stop retrying it, keep the local text.
        await this.db.notes.update(id, { dirty: 0 });
        return false;
    }
  }

  private async applyApplied(id: string, server: Note | undefined, pushed: LocalNote): Promise<void> {
    const current = await this.db.notes.get(id);
    if (!server || server.deleted || pushed.deleted) {
      if (current?.localRev === pushed.localRev || !current || current.deleted) await this.db.notes.delete(id);
      return;
    }
    if (!current || current.localRev === pushed.localRev) {
      await this.db.notes.put(noteFromProto(server, pushed.localRev));
      return;
    }
    // Edited again while the request was in flight: rebase onto the new version.
    await this.db.notes.update(id, { version: Number(server.version), role: noteFromProto(server).role, shared: server.shared });
  }

  private async applyConflict(server: Note | undefined, pushed: LocalNote): Promise<boolean> {
    const local = (await this.db.notes.get(pushed.id)) ?? pushed;
    if (!local.deleted) await this.repo.duplicate(local, this.opts.ownerId(), ' (Konflikt)');
    await this.replaceOrDrop(pushed.id, server);
    return !local.deleted;
  }

  private async replaceOrDrop(id: string, server: Note | undefined): Promise<void> {
    if (server && !server.deleted) await this.db.notes.put(noteFromProto(server));
    else await this.db.notes.delete(id);
  }

  private async applyPull(notes: Note[], removedIds: string[]): Promise<void> {
    const locals = await this.db.notes.bulkGet(notes.map((n) => n.id));
    const puts: LocalNote[] = [];
    const deletes: string[] = [...removedIds];
    for (const [i, n] of notes.entries()) {
      const local = locals[i];
      const action = pullAction(local, n);
      if (action === 'delete') deletes.push(n.id);
      else if (action === 'put') puts.push(noteFromProto(n, local?.localRev ?? 0));
      else if (action === 'access') await this.db.notes.update(n.id, { role: roleOf(n), shared: n.shared });
    }
    if (puts.length > 0) await this.db.notes.bulkPut(puts);
    if (deletes.length > 0) await this.db.notes.bulkDelete(deletes);
  }

  /** Starts background syncing (interval, online and visibility events). */
  start(intervalMs = 30_000): () => void {
    const onWake = () => {
      if (document.visibilityState === 'visible') void this.sync();
    };
    const onOffline = () => this.setState({ status: 'offline' });
    window.addEventListener('online', onWake);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onWake);
    const interval = setInterval(onWake, intervalMs);
    void this.sync();
    return () => {
      window.removeEventListener('online', onWake);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onWake);
      clearInterval(interval);
      clearTimeout(this.timer);
    };
  }

  /** Forgets the cursor (used on logout). */
  async reset(): Promise<void> {
    await this.db.meta.delete(CURSOR_KEY);
    this.setState({ status: 'idle', lastSyncAt: null, conflicts: 0 });
  }
}

const roleOf = (n: Note) => noteFromProto(n).role;

/** How a pulled note is merged into the local copy. */
function pullAction(local: LocalNote | undefined, n: Note): 'put' | 'delete' | 'access' | 'skip' {
  if (local?.dirty) {
    // Keep unsynced local edits; the next push resolves (or conflicts). Only access may change.
    return local.role === roleOf(n) && local.shared === n.shared ? 'skip' : 'access';
  }
  if (n.deleted) return local ? 'delete' : 'skip';
  // Own pushes come back in the pull; skip rewriting notes that are already up to date.
  const upToDate = local?.version === Number(n.version) && local.role === roleOf(n) && local.shared === n.shared;
  return upToDate ? 'skip' : 'put';
}
